import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getPaymentStatus } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/motion";
import { CheckCircle2, Landmark, Loader2, Truck, XCircle } from "lucide-react";

export default function PaymentResult({ type }) {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const [state, setState] = useState("loading");
  const [info, setInfo] = useState(null);
  const { clearCart } = useStore();
  const attempts = useRef(0);

  useEffect(() => {
    if (type !== "success" || !orderId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await getPaymentStatus(orderId);
        setInfo(r);
        if (r.payment_status === "paid" || r.payment_status === "kapida") {
          if (!cancelled) { setState(r.payment_status === "kapida" ? "kapida" : "paid"); clearCart(); }
          return;
        }
        if (r.payment_method === "havale" && r.payment_status === "pending") {
          if (!cancelled) { setState("havale"); clearCart(); }
          return;
        }
        if (r.payment_status === "failed") {
          if (!cancelled) setState("failed");
          return;
        }
      } catch {}
      attempts.current += 1;
      if (attempts.current < 15) setTimeout(poll, 2000);
      else if (!cancelled) setState("timeout");
    };
    poll();
    return () => { cancelled = true; };
  }, [type, orderId, clearCart]);

  if (type === "cancel") {
    return (
      <>
        <Seo title="Ödeme İptal Edildi" path="/odeme/iptal"  noindex />
        <div className="container-shule py-20 lg:py-28 flex justify-center text-center">
          <Reveal className="max-w-md">
            <XCircle className="w-14 h-14 mx-auto text-shule-taupe" />
            <h1 data-testid="payment-cancel-title" className="mt-5 font-serif text-4xl text-shule-coffee">Ödeme tamamlanmadı</h1>
            <p className="mt-4 text-shule-mocha leading-relaxed">
              Sepetin olduğu gibi duruyor; hazır olduğunda kaldığın yerden devam edebilirsin.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/sepet" data-testid="payment-cancel-cart-button" className="btn-primary-shule">Sepete Dön</Link>
              <Link to="/" data-testid="payment-cancel-home-button" className="btn-outline-shule">Alışverişe Devam Et</Link>
            </div>
          </Reveal>
        </div>
      </>
    );
  }

  const bank = info?.bank || {};

  return (
    <>
      <Seo title="Sipariş Onayı" path="/odeme/basarili"  noindex />
      <div className="container-shule py-20 lg:py-28 flex justify-center text-center">
        <Reveal className="max-w-md w-full">
          {state === "loading" && (
            <>
              <Loader2 data-testid="payment-loading" className="w-14 h-14 mx-auto text-shule-gold animate-spin" />
              <h1 className="mt-5 font-serif text-4xl text-shule-coffee">Siparişin doğrulanıyor</h1>
              <p className="mt-4 text-shule-mocha">Birkaç saniye içinde hazır…</p>
            </>
          )}
          {(state === "paid" || state === "kapida") && (
            <>
              {state === "kapida"
                ? <Truck data-testid="payment-success-icon" className="w-14 h-14 mx-auto text-shule-gold" />
                : <CheckCircle2 data-testid="payment-success-icon" className="w-14 h-14 mx-auto text-shule-gold" />}
              <h1 data-testid="payment-success-title" className="mt-5 font-serif text-4xl text-shule-coffee">
                Siparişin alındı
              </h1>
              {orderId && <p data-testid="payment-order-id" className="mt-2 text-sm text-shule-soft">Sipariş no: <span className="font-mono font-semibold">#{orderId}</span></p>}
              <p className="mt-4 text-shule-mocha leading-relaxed">
                {state === "kapida"
                  ? "Ödemeni sipariş teslimatında seçtiğin kapıda ödeme yöntemiyle tamamlayabilirsin. Sipariş durumunu hesabından takip edebilirsin."
                  : "Ödemen onaylandı. Sipariş durumunu hesabından takip edebilirsin."}
              </p>
              <Link to="/" data-testid="payment-success-home-button" className="btn-primary-shule mt-8">Ana Sayfaya Dön</Link>
            </>
          )}
          {state === "havale" && (
            <>
              <Landmark data-testid="payment-havale-icon" className="w-14 h-14 mx-auto text-shule-gold" />
              <h1 data-testid="payment-havale-title" className="mt-5 font-serif text-4xl text-shule-coffee">
                Siparişin oluşturuldu
              </h1>
              {orderId && <p data-testid="payment-order-id" className="mt-2 text-sm text-shule-soft">Sipariş no: <span className="font-mono font-semibold">#{orderId}</span></p>}
              {bank.iban ? (
                <div data-testid="havale-bank-info" className="mt-6 rounded-3xl border border-shule-line bg-shule-paper p-5 text-left text-sm">
                  <p className="font-semibold text-shule-coffee">{bank.bank}</p>
                  <p className="text-shule-mocha mt-1">{bank.holder}</p>
                  <p className="font-mono font-semibold mt-2 select-all">{bank.iban}</p>
                  <p className="text-xs text-shule-soft mt-3">Açıklama kısmına sipariş numaranı (#{orderId}) yazmayı unutma.</p>
                </div>
              ) : (
                <p data-testid="havale-bank-info" className="mt-4 text-shule-mocha leading-relaxed">
                  Banka bilgileri hesabında tanımlandığında burada gösterilir. Ödeme yapmadan önce bilgileri doğrula.
                </p>
              )}
              <p className="mt-4 text-sm text-shule-soft">Ödemen ulaştığında siparişin hazırlanmaya başlar.</p>
              <Link to="/" data-testid="payment-success-home-button" className="btn-primary-shule mt-8">Ana Sayfaya Dön</Link>
            </>
          )}
          {state === "failed" && (
            <>
              <XCircle data-testid="payment-failed-icon" className="w-14 h-14 mx-auto text-shule-taupe" />
              <h1 data-testid="payment-failed-title" className="mt-5 font-serif text-4xl text-shule-coffee">Ödeme başarısız</h1>
              <p className="mt-4 text-shule-mocha leading-relaxed">
                Kart ödemen tamamlanamadı. Sepetin duruyor; dilersen kapıda ödeme ya da havale/EFT ile devam edebilirsin.
              </p>
              <Link to="/sepet" data-testid="payment-failed-cart-button" className="btn-primary-shule mt-8">Sepete Dön</Link>
            </>
          )}
          {state === "timeout" && (
            <>
              <Loader2 className="w-14 h-14 mx-auto text-shule-taupe" />
              <h1 data-testid="payment-timeout-title" className="mt-5 font-serif text-4xl text-shule-coffee">Onay biraz uzun sürdü</h1>
              <p className="mt-4 text-shule-mocha leading-relaxed">
                Ödeme sonucu henüz kesinleşmedi. Yeniden ödeme yapmadan önce sipariş durumunu kontrol et veya bizimle iletişime geç.
              </p>
              <Link to="/iletisim" data-testid="payment-timeout-contact-button" className="btn-outline-shule mt-8">Bize Ulaş</Link>
            </>
          )}
        </Reveal>
      </div>
    </>
  );
}
